import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Copy,
  CheckCircle2,
  Download,
  ExternalLink,
  Mail,
  Newspaper,
  Radio,
  Globe2,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { LOCALES } from "@/locales/landing";

/**
 * PressKitPage
 * =============
 * Public press-kit page for ethnic newspapers, community radio, YouTubers,
 * and immigration-focused newsletters. Renders:
 *   • 7 shareable localized landing-page URLs with copy-to-clipboard
 *   • Downloadable logos + brand assets
 *   • Boilerplate "About DS-260USA.com" copy in each of the 7 languages
 *   • Ready-to-publish press-release drafts in each of the 7 languages
 *   • Media contact block
 *
 * NOTE: The press-release drafts are seed copy — the user can (and should)
 * refine each language with a native reviewer before publishing.
 */

const PRESS_LANGS = ["en", "es", "hi", "zh", "vi", "tl", "ko"];

// -------- Boilerplate ("About DS-260USA.com") copy in each language --------
const BOILERPLATE = {
  en: `About DS-260USA.com — DS-260USA.com is a documents-first family immigration platform designed by a licensed U.S. immigration attorney with 35+ years of experience. Instead of asking families to retype names, dates, and places of birth into Form DS-260, the platform reads a family's passports, birth certificates, marriage certificates, and IDs in 15+ languages and auto-populates the required NVC forms. Every field is shown next to its source document so families can double-check before filing. DS-260USA.com is not a law firm.`,

  es: `Sobre DS-260USA.com — DS-260USA.com es una plataforma de inmigración familiar centrada en documentos, diseñada por un abogado de inmigración licenciado en EE. UU. con más de 35 años de experiencia. En lugar de pedir a las familias que vuelvan a escribir nombres, fechas y lugares de nacimiento en los formularios DS-260, la plataforma lee los pasaportes, actas de nacimiento, actas de matrimonio e identificaciones de la familia en más de 15 idiomas y llena automáticamente los formularios requeridos por NVC. Cada campo se muestra junto al documento del que fue extraído, para que las familias puedan revisarlo antes de presentarlo. DS-260USA.com no es un bufete de abogados.`,

  hi: `DS-260USA.com के बारे में — DS-260USA.com एक "दस्तावेज़-आधारित" पारिवारिक इमिग्रेशन प्लेटफ़ॉर्म है, जिसे 35+ वर्षों के अनुभव वाले लाइसेंस प्राप्त अमेरिकी इमिग्रेशन अटॉर्नी द्वारा डिज़ाइन किया गया है। परिवारों से Form DS-260 में नाम, तारीखें और जन्मस्थान बार-बार टाइप करवाने के बजाय, यह प्लेटफ़ॉर्म 15+ भाषाओं में परिवार के पासपोर्ट, जन्म प्रमाणपत्र, विवाह प्रमाणपत्र और पहचान पत्र पढ़ता है और आवश्यक NVC फॉर्म स्वचालित रूप से भर देता है। प्रत्येक फ़ील्ड उस दस्तावेज़ के साथ दिखाया जाता है जहाँ से वह लिया गया है, ताकि दाख़िले से पहले परिवार जाँच सके। DS-260USA.com एक लॉ फर्म नहीं है।`,

  zh: `关于 DS-260USA.com — DS-260USA.com 是一个"以文件为先"的家庭移民平台,由一位拥有 35 年以上执业经验的持牌美国移民律师设计。它不再要求家庭把姓名、日期和出生地反复输入 DS-260 表格,而是通过读取家庭的护照、出生证、结婚证和身份证件(支持 15 种以上语言),自动填写 NVC 所需的表格。每一项信息都会与其来源文件并排显示,家庭可以在递交前一一核对。DS-260USA.com 不是律师事务所。`,

  vi: `Về DS-260USA.com — DS-260USA.com là nền tảng di trú diện gia đình theo hướng "giấy tờ trước hết", được thiết kế bởi một luật sư di trú Hoa Kỳ có giấy phép hành nghề với hơn 35 năm kinh nghiệm. Thay vì yêu cầu các gia đình gõ đi gõ lại tên, ngày tháng và nơi sinh vào mẫu DS-260, nền tảng đọc hộ chiếu, giấy khai sinh, giấy đăng ký kết hôn và giấy tờ tùy thân của gia đình bằng hơn 15 ngôn ngữ, rồi tự động điền các mẫu NVC bắt buộc. Mỗi trường thông tin được hiển thị cạnh giấy tờ gốc để gia đình kiểm tra trước khi nộp. DS-260USA.com không phải là một công ty luật.`,

  tl: `Tungkol sa DS-260USA.com — Ang DS-260USA.com ay isang "documents-first" na family immigration platform na dinesenyo ng isang licensed U.S. immigration attorney na may mahigit 35 taong karanasan. Sa halip na hilingin sa mga pamilya na muling i-type ang mga pangalan, petsa, at lugar ng kapanganakan sa Form DS-260, binabasa ng platform ang mga pasaporte, birth certificate, marriage certificate, at IDs ng pamilya sa mahigit 15 wika at awtomatikong pinupuno ang mga kinakailangang porma ng NVC. Bawat field ay ipinapakita katabi ng dokumentong pinagmulan nito, para masuri ng pamilya bago mag-file. Ang DS-260USA.com ay hindi isang law firm.`,

  ko: `DS-260USA.com 소개 — DS-260USA.com는 35년 이상의 경력을 지닌 미국 라이선스 이민 변호사가 설계한 '서류 우선' 가족 이민 플랫폼입니다. 가족에게 이름, 날짜, 출생지를 DS-260 양식에 계속 다시 입력하게 하는 대신, 이 플랫폼은 가족의 여권, 출생증명서, 결혼증명서, 신분증을 15개 이상의 언어로 읽어 필요한 NVC 양식을 자동으로 채웁니다. 각 항목은 그 정보를 가져온 원본 서류 옆에 함께 표시되므로, 가족은 제출 전에 직접 검토할 수 있습니다. DS-260USA.com는 로펌이 아닙니다.`,
};

// -------- Press-release seed drafts in each language --------
// These are seed drafts. Editors can and should refine language with a native
// reviewer before publishing.
const PRESS_RELEASE = {
  en: {
    headline: "New Immigration Platform Lets Families Skip the Paperwork — Just Upload Your Documents",
    subhead:
      "Designed by a U.S. immigration attorney with 35+ years of experience, DS-260USA.com reads passports and birth certificates in 15+ languages and auto-populates Form DS-260.",
    dateline: "UNITED STATES —",
    body: `Filing a family immigrant-visa (consular) case has long meant weeks of retyping names, dates, and places of birth into government forms — a process that can delay reunification for months when small mistakes trigger NVC Requests for Evidence. DS-260USA.com, a new documents-first immigration platform, changes that: families simply upload photos of their passports, birth certificates, marriage certificates, and IDs, and the platform reads the documents in more than 15 languages, extracts the required information, and auto-populates Form DS-260.

Every field is displayed next to the document it came from, so families can catch mismatches before filing. Users retain full control — no forms are submitted without their approval — and an optional read-only review by a licensed U.S. immigration attorney can be requested at any time.

The platform is available today at ds-260usa.com. A free tier lets families upload documents, review their case, and preview their filing packet before paying to download the signed, mail-ready NVC submission.`,
    quote:
      "\"We built this platform to help families avoid the common paperwork mistakes we've seen in over three decades of family-based immigration practice — mistakes that can delay reunification by months or years.\"",
    quote_attrib: "— Designed by a Licensed U.S. Immigration Attorney with 35+ years of experience",
  },

  es: {
    headline: "Nueva plataforma de inmigración permite a las familias saltarse el papeleo — solo suban sus documentos",
    subhead:
      "Diseñada por un abogado de inmigración de EE. UU. con más de 35 años de experiencia, DS-260USA.com lee pasaportes y actas de nacimiento en más de 15 idiomas y llena automáticamente el Formulario DS-260.",
    dateline: "ESTADOS UNIDOS —",
    body: `Presentar una caso de visa de inmigrante familiar (proceso consular) ha significado durante mucho tiempo semanas de reescribir nombres, fechas y lugares de nacimiento en los formularios del gobierno — un proceso que puede retrasar la reunificación por meses cuando pequeños errores provocan Solicitudes de Evidencia (RFE) de NVC. DS-260USA.com, una nueva plataforma de inmigración centrada en documentos, cambia eso: las familias simplemente suben fotos de sus pasaportes, actas de nacimiento, actas de matrimonio e identificaciones, y la plataforma lee los documentos en más de 15 idiomas, extrae la información requerida y llena automáticamente los formularios DS-260.

Cada campo se muestra junto al documento del que proviene, para que las familias detecten discrepancias antes de presentar la petición. Los usuarios mantienen el control total — no se envía ningún formulario sin su aprobación — y en cualquier momento se puede solicitar una revisión opcional, de solo lectura, por parte de un abogado de inmigración licenciado.

La plataforma ya está disponible en ds-260usa.com. Un nivel gratuito permite a las familias subir documentos, revisar su caso y previsualizar su paquete de presentación antes de pagar para descargar el envío firmado y listo para enviar a NVC.`,
    quote:
      "\"Construimos esta plataforma para ayudar a las familias a evitar los errores de papeleo que hemos visto en más de tres décadas de práctica en inmigración familiar — errores que pueden retrasar la reunificación por meses o años.\"",
    quote_attrib: "— Diseñado por un abogado de inmigración licenciado en EE. UU. con más de 35 años de experiencia",
  },

  hi: {
    headline: "नई इमिग्रेशन प्लेटफ़ॉर्म: कागज़ी काम छोड़िए — बस अपने दस्तावेज़ अपलोड कीजिए",
    subhead:
      "35+ वर्षों के अनुभव वाले अमेरिकी इमिग्रेशन अटॉर्नी द्वारा डिज़ाइन, DS-260USA.com पासपोर्ट और जन्म प्रमाणपत्र 15+ भाषाओं में पढ़ता है और Form DS-260 स्वचालित रूप से भर देता है।",
    dateline: "संयुक्त राज्य अमेरिका —",
    body: `परिवार-आधारित इमिग्रेंट वीज़ा (DS-260) केस दाखिल करना लंबे समय से हफ़्तों तक सरकारी फ़ॉर्म में नाम, तारीखें और जन्मस्थान बार-बार टाइप करने का काम रहा है — एक ऐसी प्रक्रिया जिसमें छोटी-सी गलती NVC Request for Evidence (RFE) ला सकती है और परिवार के मिलन को महीनों तक टाल सकती है। DS-260USA.com, एक नया "दस्तावेज़-आधारित" इमिग्रेशन प्लेटफ़ॉर्म, इसे बदलता है: परिवार अपने पासपोर्ट, जन्म प्रमाणपत्र, विवाह प्रमाणपत्र और पहचान पत्र की तस्वीरें अपलोड करते हैं, और प्लेटफ़ॉर्म 15+ भाषाओं में दस्तावेज़ पढ़कर आवश्यक जानकारी निकालता है और DS-260 फ़ॉर्म स्वचालित रूप से भर देता है।

हर फ़ील्ड उस दस्तावेज़ के साथ दिखाया जाता है जहाँ से वह लिया गया है, ताकि दाख़िले से पहले परिवार गड़बड़ियाँ पकड़ सके। उपयोगकर्ता पूरे नियंत्रण में रहते हैं — कोई भी फ़ॉर्म उनकी स्वीकृति के बिना जमा नहीं होता — और कभी भी लाइसेंस प्राप्त अमेरिकी इमिग्रेशन अटॉर्नी द्वारा वैकल्पिक "केवल-पठन" समीक्षा माँगी जा सकती है।

प्लेटफ़ॉर्म आज ds-260usa.com पर उपलब्ध है। एक मुफ्त स्तर परिवारों को दस्तावेज़ अपलोड करने, अपने केस की समीक्षा करने और NVC के लिए तैयार, हस्ताक्षरित पैकेट डाउनलोड करने से पहले पूर्वावलोकन करने की अनुमति देता है।`,
    quote:
      "\"हमने यह मंच परिवारों को उन कागज़ी गलतियों से बचाने के लिए बनाया है, जिन्हें हम पारिवारिक इमिग्रेशन प्रैक्टिस के तीन दशकों से अधिक समय में देख रहे हैं — ऐसी गलतियाँ जो परिवार के मिलन में महीनों या वर्षों की देरी कर सकती हैं।\"",
    quote_attrib: "— 35+ वर्षों के अनुभव वाले लाइसेंस प्राप्त अमेरिकी इमिग्रेशन अटॉर्नी द्वारा डिज़ाइन",
  },

  zh: {
    headline: "全新移民平台让家庭跳过繁琐文书 —— 只需上传文件",
    subhead:
      "由拥有 35 年以上执业经验的美国持牌移民律师设计,DS-260USA.com 能识别 15+ 种语言的护照与出生证件,并自动填写 DS-260 表格。",
    dateline: "美国 —",
    body: `递交亲属类移民签证(DS-260)申请长期以来意味着数周的时间要在政府表格上反复输入姓名、日期和出生地——这一过程中的一个小错误就可能触发 NVC 的补件通知(RFE),导致家庭团聚被推迟数月。全新推出的"文件优先"移民平台 DS-260USA.com 改变了这一切:家庭只需上传护照、出生证、结婚证和身份证件的照片,平台就会自动读取 15 种以上语言的文件、提取所需信息,并自动填写 DS-260 表格。

每一项信息都会与其来源文件并排显示,以便家庭在递交前一一核对。用户始终握有主动权 —— 未经用户批准不会提交任何表格 —— 且随时可以申请由持牌美国移民律师进行的可选"只读审阅"。

该平台今日已在 ds-260usa.com 上线。免费级别允许家庭上传文件、审阅案件,并在付费下载已签署、可寄送 NVC 的完整申请包之前预览申请内容。`,
    quote:
      "\"我们打造这个平台,是为了帮助家庭避免我们在三十多年家庭移民实务中反复看到的那些常见文件错误 —— 这些错误可能让家人团聚推迟数月甚至数年。\"",
    quote_attrib: "— 由持牌美国移民律师设计,35 年以上执业经验",
  },

  vi: {
    headline: "Nền tảng di trú mới giúp các gia đình bỏ qua giấy tờ — chỉ cần tải lên tài liệu",
    subhead:
      "Được thiết kế bởi một luật sư di trú Hoa Kỳ có giấy phép hành nghề với hơn 35 năm kinh nghiệm, DS-260USA.com đọc hộ chiếu và giấy khai sinh bằng hơn 15 ngôn ngữ và tự động điền Mẫu DS-260.",
    dateline: "HOA KỲ —",
    body: `Nộp đơn xin thị thực định cư diện gia đình (DS-260) từ lâu đã đồng nghĩa với hàng tuần gõ đi gõ lại tên, ngày tháng và nơi sinh vào các mẫu đơn của chính phủ — một quá trình mà chỉ một sai sót nhỏ cũng có thể dẫn đến Yêu cầu Bổ sung Chứng cứ (RFE) từ NVC và làm việc đoàn tụ gia đình bị trì hoãn hàng tháng. DS-260USA.com, một nền tảng di trú "giấy tờ trước hết" hoàn toàn mới, thay đổi điều đó: các gia đình chỉ cần tải lên ảnh chụp hộ chiếu, giấy khai sinh, giấy đăng ký kết hôn và giấy tờ tùy thân, nền tảng sẽ đọc tài liệu bằng hơn 15 ngôn ngữ, trích xuất thông tin cần thiết và tự động điền Mẫu DS-260.

Mỗi trường thông tin được hiển thị cạnh giấy tờ gốc, để gia đình có thể phát hiện những điểm không khớp trước khi nộp. Người dùng vẫn hoàn toàn kiểm soát — không có mẫu đơn nào được gửi đi mà không có sự chấp thuận của họ — và có thể yêu cầu một buổi xem xét chỉ đọc tùy chọn bởi một luật sư di trú Hoa Kỳ có giấy phép vào bất cứ lúc nào.

Nền tảng đã có sẵn hôm nay tại ds-260usa.com. Cấp miễn phí cho phép gia đình tải lên tài liệu, xem xét hồ sơ và xem trước bộ hồ sơ trước khi trả tiền để tải xuống bộ hồ sơ đã ký và sẵn sàng gửi NVC.`,
    quote:
      "\"Chúng tôi xây dựng nền tảng này để giúp các gia đình tránh những sai sót về giấy tờ phổ biến mà chúng tôi đã thấy trong hơn ba thập kỷ hành nghề di trú diện gia đình — những sai sót có thể khiến việc đoàn tụ bị trì hoãn hàng tháng hoặc hàng năm.\"",
    quote_attrib: "— Được thiết kế bởi một luật sư di trú Hoa Kỳ có giấy phép hành nghề với hơn 35 năm kinh nghiệm",
  },

  tl: {
    headline: "Bagong Immigration Platform, Puwedeng Laktawan ng mga Pamilya ang Papeles — I-upload Lang ang mga Dokumento",
    subhead:
      "Dinesenyo ng isang licensed U.S. immigration attorney na may mahigit 35 taong karanasan, binabasa ng DS-260USA.com ang mga pasaporte at birth certificate sa mahigit 15 wika at awtomatikong pinupuno ang Form DS-260.",
    dateline: "ESTADOS UNIDOS —",
    body: `Ang paghain ng family immigrant-visa (consular) case ay matagal nang nangangahulugan ng ilang linggo ng muling pagta-type ng mga pangalan, petsa, at lugar ng kapanganakan sa mga porma ng pamahalaan — isang proseso kung saan ang isang maliit na pagkakamali ay maaaring magbunga ng NVC Request for Evidence (RFE) at maantala nang ilang buwan ang muling pagsasama ng pamilya. Binabago ito ng DS-260USA.com, isang bagong "documents-first" na immigration platform: nag-a-upload lang ang mga pamilya ng mga larawan ng kanilang mga pasaporte, birth certificate, marriage certificate, at IDs, at binabasa ng platform ang mga dokumento sa mahigit 15 wika, kinukuha ang kinakailangang impormasyon, at awtomatikong pinupuno ang Form DS-260.

Bawat field ay ipinapakita katabi ng dokumentong pinagmulan nito, kaya mahuhuli ng mga pamilya ang anumang hindi pagkakatugma bago mag-file. Nasa kamay ng gumagamit ang buong kontrol — walang porma na maisusumite nang walang pahintulot nila — at maaari silang humiling ng opsyonal na read-only na pagsusuri ng isang licensed U.S. immigration attorney anumang oras.

Available na ngayon ang platform sa ds-260usa.com. May libreng tier na nagpapahintulot sa mga pamilya na mag-upload ng mga dokumento, suriin ang kanilang kaso, at i-preview ang kanilang filing packet bago magbayad para i-download ang pirmadong, handa nang ipadalang NVC submission.`,
    quote:
      "\"Ginawa namin ang platform na ito upang matulungan ang mga pamilya na maiwasan ang mga karaniwang pagkakamali sa mga papel na nakita namin sa mahigit tatlong dekada ng family-based immigration practice — mga pagkakamali na maaaring magpaantala sa muling pagsasama ng pamilya nang ilang buwan o taon.\"",
    quote_attrib: "— Dinesenyo ng isang licensed U.S. immigration attorney na may mahigit 35 taong karanasan",
  },

  ko: {
    headline: "새로운 이민 플랫폼, 서류 작업은 이제 그만 — 문서만 업로드하세요",
    subhead:
      "35년 이상의 경력을 지닌 미국 라이선스 이민 변호사가 설계한 DS-260USA.com는 여권과 출생증명서를 15개 이상의 언어로 읽어 DS-260 양식을 자동으로 채워줍니다.",
    dateline: "미국 —",
    body: `가족 초청 이민 비자(DS-260) 신청서를 준비하는 일은 오랫동안 정부 양식에 이름·날짜·출생지를 몇 주에 걸쳐 반복해서 입력하는 작업을 의미했습니다. 작은 실수 하나가 NVC의 추가 서류 요청(RFE)을 유발해 가족 재결합을 몇 달씩 지연시키기도 합니다. 새로운 '서류 우선' 이민 플랫폼 DS-260USA.com는 이 방식을 바꿉니다. 가족이 여권, 출생증명서, 결혼증명서, 신분증 사진만 업로드하면, 플랫폼이 15개 이상의 언어로 서류를 읽어 필요한 정보를 추출하고 DS-260 양식을 자동으로 채웁니다.

각 항목은 정보를 가져온 원본 서류 옆에 함께 표시되어, 가족이 제출 전에 불일치를 직접 확인할 수 있습니다. 사용자는 항상 주도권을 가집니다 — 승인 없이는 어떤 양식도 제출되지 않으며, 언제든지 미국 라이선스 이민 변호사의 '읽기 전용' 검토를 선택적으로 요청할 수 있습니다.

플랫폼은 오늘부터 ds-260usa.com 에서 이용할 수 있습니다. 무료 요금제는 서류를 업로드하고, 사건을 검토하고, 서명된 우편 발송용 NVC 제출 패킷을 다운로드하기 전에 미리보기까지 할 수 있도록 지원합니다.`,
    quote:
      "\"저희는 가족 기반 이민 실무를 30년 넘게 하면서 계속 보아온 흔한 서류 실수들로부터 가족들을 지키기 위해 이 플랫폼을 만들었습니다 — 그런 실수들은 가족 재결합을 몇 달, 심지어 몇 년까지 지연시킬 수 있습니다.\"",
    quote_attrib: "— 35년 이상의 경력을 지닌 미국 라이선스 이민 변호사가 설계",
  },
};

// UI label i18n (kept simple; page chrome stays English since it's an editor-facing tool)
const UI_LABELS = {
  h1: "Press Kit",
  intro:
    "Everything a reporter, editor, community-radio host, or newsletter writer needs to cover DS-260USA.com — in seven languages.",
  urlsTitle: "Shareable localized landing pages",
  urlsSub:
    "Send your community a link in their language. Each URL is tagged with UTM parameters so we can see which press channel converted visitors.",
  logosTitle: "Logos & brand assets",
  logosSub: "Right-click → Save As, or use the download buttons below.",
  boilerplateTitle: 'Boilerplate "About" copy',
  boilerplateSub:
    "Drop this at the bottom of a story. One paragraph per language — pre-cleared for publication.",
  pressReleaseTitle: "Ready-to-publish press releases",
  pressReleaseSub:
    "Seed drafts in each of the 7 languages. Copy the full release, then edit the dateline, city, and any local details before publishing.",
  contactTitle: "Media contact",
  contactSub: "For interview requests, screenshots, product demos, or embargoed access.",
  usageTitle: "Usage guidelines (short version)",
  brandName: "DS-260USA.com",
  tagline: "Turn Your Family's Documents Into Completed DS-260 Cases — Automatically",
};

// -------- Reusable copy-to-clipboard button --------
function CopyButton({ text, label = "Copy", testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't access the clipboard. Please copy manually.");
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition"
      style={{
        borderColor: "var(--border-stone)",
        background: copied ? "#EFF9F1" : "#FFFFFF",
        color: copied ? "#065F46" : "var(--text-primary)",
      }}
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : label}
    </button>
  );
}

// -------- Language tab picker --------
function LangTabs({ active, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2 mb-5">
      {PRESS_LANGS.map((code) => {
        const meta = LOCALES.find((l) => l.code === code);
        const isActive = active === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => onChange(code)}
            data-testid={`${testidPrefix}-tab-${code}`}
            className="text-sm font-semibold px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5"
            style={{
              borderColor: isActive ? "var(--action)" : "var(--border-stone)",
              background: isActive ? "var(--action)" : "#FFFFFF",
              color: isActive ? "#FFFFFF" : "var(--text-primary)",
            }}
          >
            <span>{meta?.flag}</span>
            <span>{meta?.nativeName}</span>
          </button>
        );
      })}
    </div>
  );
}

// -------- Individual sections --------
function TopBar() {
  return (
    <header
      className="border-b sticky top-0 z-10"
      style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
    >
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="presskit-home-link">
          <div className="font-display font-extrabold text-lg" style={{ color: "var(--text-primary)" }}>
            DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
          </div>
          <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>
            Consular Processing
          </span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/" className="hidden sm:inline" style={{ color: "var(--text-secondary)" }}>
            Home
          </Link>
          <a
            href="mailto:press@ds-260usa.com"
            className="inline-flex items-center gap-1.5 btn-primary text-sm px-4 py-2"
            data-testid="presskit-contact-btn"
          >
            <Mail className="w-3.5 h-3.5" />
            press@ds-260usa.com
          </a>
        </div>
      </div>
    </header>
  );
}

function PressKitHero() {
  return (
    <section className="py-16 sm:py-20" style={{ background: "var(--bg-app)" }}>
      <div className="mx-auto max-w-4xl px-6 text-center">
        <div className="pill pill-ai mb-4 inline-flex items-center gap-1.5">
          <Newspaper className="w-3.5 h-3.5" /> For editors, reporters & community media
        </div>
        <h1
          className="font-display text-4xl sm:text-5xl font-extrabold mb-4"
          style={{ color: "var(--text-primary)" }}
        >
          {UI_LABELS.h1}
        </h1>
        <p className="text-[16px] leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--text-secondary)" }}>
          {UI_LABELS.intro}
        </p>
        <p
          className="text-sm mt-3 italic max-w-xl mx-auto"
          style={{ color: "var(--text-secondary)" }}
        >
          {UI_LABELS.tagline}
        </p>
      </div>
    </section>
  );
}

function ShareableUrlsSection({ origin }) {
  return (
    <section
      className="py-16 border-t"
      style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      data-testid="presskit-urls-section"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-8">
          <div className="pill pill-ai mb-3 inline-flex items-center gap-1.5">
            <Globe2 className="w-3.5 h-3.5" /> Localized URLs
          </div>
          <h2
            className="font-display text-2xl sm:text-3xl font-extrabold mb-2"
            style={{ color: "var(--text-primary)" }}
          >
            {UI_LABELS.urlsTitle}
          </h2>
          <p className="text-sm max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            {UI_LABELS.urlsSub}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRESS_LANGS.map((code) => {
            const meta = LOCALES.find((l) => l.code === code);
            const path = `/press/${code}`;
            const fullUrl = `${origin}${path}?utm_source=press&utm_medium=community&utm_campaign=press_${code}`;
            return (
              <div
                key={code}
                className="card-surface p-5 flex flex-col gap-3"
                data-testid={`presskit-url-card-${code}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{meta?.flag}</span>
                  <div>
                    <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>
                      {meta?.nativeName}
                    </div>
                    <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                      {meta?.name}
                    </div>
                  </div>
                </div>
                <div
                  className="text-[12px] break-all rounded p-2"
                  style={{
                    background: "#FBF6EC",
                    color: "var(--text-primary)",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  }}
                >
                  {fullUrl}
                </div>
                <div className="flex items-center gap-2">
                  <CopyButton
                    text={fullUrl}
                    label="Copy URL"
                    testid={`presskit-copy-url-${code}`}
                  />
                  <a
                    href={path}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border"
                    style={{
                      borderColor: "var(--border-stone)",
                      background: "#FFFFFF",
                      color: "var(--action)",
                    }}
                    data-testid={`presskit-open-url-${code}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Preview
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LogosSection({ origin }) {
  const assets = [
    { file: "/icon.svg", label: "Wordmark (SVG)", size: "vector" },
    { file: "/icon-512.png", label: "App icon (512×512 PNG)", size: "square" },
    { file: "/icon-192.png", label: "App icon (192×192 PNG)", size: "square" },
    { file: "/apple-touch-icon.png", label: "Apple touch icon (180×180 PNG)", size: "square" },
    { file: "/favicon.png", label: "Favicon (PNG)", size: "small" },
  ];
  return (
    <section className="py-16" style={{ background: "var(--bg-app)" }} data-testid="presskit-logos-section">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-8">
          <div className="pill pill-ai mb-3 inline-flex">Brand assets</div>
          <h2
            className="font-display text-2xl sm:text-3xl font-extrabold mb-2"
            style={{ color: "var(--text-primary)" }}
          >
            {UI_LABELS.logosTitle}
          </h2>
          <p className="text-sm max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            {UI_LABELS.logosSub}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {assets.map((a) => (
            <div
              key={a.file}
              className="card-surface p-5 flex items-center gap-4"
              data-testid={`presskit-asset-${a.file.replace(/[\/\.]/g, "-")}`}
              style={{ background: "#FFFFFF" }}
            >
              <div
                className="rounded-md flex items-center justify-center flex-shrink-0"
                style={{
                  width: 72,
                  height: 72,
                  background: "#FBF6EC",
                  border: "1px solid var(--border-stone)",
                }}
              >
                <img
                  src={a.file}
                  alt={a.label}
                  style={{ maxWidth: 56, maxHeight: 56, objectFit: "contain" }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm mb-1" style={{ color: "var(--text-primary)" }}>
                  {a.label}
                </div>
                <div className="text-[11px] mb-2 break-all" style={{ color: "var(--text-secondary)" }}>
                  {origin}
                  {a.file}
                </div>
                <a
                  href={a.file}
                  download
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border"
                  style={{
                    borderColor: "var(--border-stone)",
                    background: "#FFFFFF",
                    color: "var(--action)",
                  }}
                  data-testid={`presskit-download-${a.file.replace(/[\/\.]/g, "-")}`}
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Usage guidelines */}
        <div
          className="card-surface mt-8 p-6"
          style={{ background: "#FFFFFF" }}
          data-testid="presskit-usage-guidelines"
        >
          <div className="font-display font-bold text-sm mb-3" style={{ color: "var(--text-primary)" }}>
            {UI_LABELS.usageTitle}
          </div>
          <ul className="text-sm space-y-2" style={{ color: "var(--text-secondary)" }}>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#065F46" }} />
              Use the brand name as <strong>DS-260USA.com</strong> (with the lowercase <code>.com</code>) — never
              "DS260USA" or "DS-260 USA".
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#065F46" }} />
              Do not recolor the wordmark. If contrast is a problem, use the light or dark background variant.
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#065F46" }} />
              DS-260USA.com <strong>is not a law firm</strong> — please retain that disclaimer in any coverage.
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function BoilerplateSection() {
  const [active, setActive] = useState("en");
  const meta = LOCALES.find((l) => l.code === active);
  const copy = BOILERPLATE[active];
  return (
    <section
      className="py-16 border-t"
      style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      data-testid="presskit-boilerplate-section"
    >
      <div className="mx-auto max-w-4xl px-6">
        <div className="mb-6">
          <div className="pill pill-ai mb-3 inline-flex">Boilerplate</div>
          <h2
            className="font-display text-2xl sm:text-3xl font-extrabold mb-2"
            style={{ color: "var(--text-primary)" }}
          >
            {UI_LABELS.boilerplateTitle}
          </h2>
          <p className="text-sm max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            {UI_LABELS.boilerplateSub}
          </p>
        </div>

        <LangTabs active={active} onChange={setActive} testidPrefix="presskit-boilerplate" />

        <div
          className="card-surface p-6"
          style={{ background: "#FBF6EC" }}
          data-testid={`presskit-boilerplate-content-${active}`}
        >
          <div className="flex justify-between items-start gap-4 mb-3">
            <div
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: "var(--text-secondary)" }}
            >
              {meta?.flag} {meta?.nativeName} ({meta?.name})
            </div>
            <CopyButton text={copy} label="Copy boilerplate" testid={`presskit-copy-boilerplate-${active}`} />
          </div>
          <p
            className="text-[15px] leading-relaxed whitespace-pre-line"
            style={{ color: "var(--text-primary)" }}
          >
            {copy}
          </p>
        </div>
      </div>
    </section>
  );
}

function PressReleaseSection() {
  const [active, setActive] = useState("en");
  const meta = LOCALES.find((l) => l.code === active);
  const draft = PRESS_RELEASE[active];

  const fullReleaseText = useMemo(() => {
    return `${draft.headline}

${draft.subhead}

${draft.dateline} ${draft.body}

${draft.quote}
${draft.quote_attrib}

---
${BOILERPLATE[active]}

Media contact:
press@ds-260usa.com`;
  }, [active, draft]);

  return (
    <section
      className="py-16"
      style={{ background: "var(--bg-app)" }}
      data-testid="presskit-release-section"
    >
      <div className="mx-auto max-w-4xl px-6">
        <div className="mb-6">
          <div className="pill pill-ai mb-3 inline-flex items-center gap-1.5">
            <Radio className="w-3.5 h-3.5" /> Press release drafts
          </div>
          <h2
            className="font-display text-2xl sm:text-3xl font-extrabold mb-2"
            style={{ color: "var(--text-primary)" }}
          >
            {UI_LABELS.pressReleaseTitle}
          </h2>
          <p className="text-sm max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            {UI_LABELS.pressReleaseSub}
          </p>
        </div>

        <LangTabs active={active} onChange={setActive} testidPrefix="presskit-release" />

        <div
          className="card-surface p-6 sm:p-8"
          style={{ background: "#FFFFFF" }}
          data-testid={`presskit-release-content-${active}`}
        >
          <div className="flex flex-wrap justify-between items-start gap-3 mb-5">
            <div
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: "var(--text-secondary)" }}
            >
              {meta?.flag} {meta?.nativeName} ({meta?.name}) · seed draft
            </div>
            <div className="flex items-center gap-2">
              <CopyButton
                text={fullReleaseText}
                label="Copy full release"
                testid={`presskit-copy-release-${active}`}
              />
              <Link
                to={`/press/${active}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border"
                style={{
                  borderColor: "var(--border-stone)",
                  background: "#FFFFFF",
                  color: "var(--action)",
                }}
                data-testid={`presskit-open-release-${active}`}
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open landing page
              </Link>
            </div>
          </div>

          <h3
            className="font-display font-extrabold text-2xl mb-3"
            style={{ color: "var(--text-primary)" }}
          >
            {draft.headline}
          </h3>
          <p
            className="text-[15px] leading-relaxed mb-5"
            style={{ color: "var(--text-secondary)" }}
          >
            {draft.subhead}
          </p>
          <div
            className="text-[15px] leading-relaxed whitespace-pre-line mb-5"
            style={{ color: "var(--text-primary)" }}
          >
            <strong>{draft.dateline}</strong> {draft.body}
          </div>
          <blockquote
            className="border-l-4 pl-4 my-6 italic"
            style={{ borderColor: "var(--action)", color: "var(--text-primary)" }}
          >
            <div className="text-[15px] leading-relaxed">{draft.quote}</div>
            <div
              className="text-xs mt-2 not-italic font-semibold"
              style={{ color: "var(--text-secondary)" }}
            >
              {draft.quote_attrib}
            </div>
          </blockquote>
          <hr style={{ borderColor: "var(--border-stone)", margin: "1.5rem 0" }} />
          <div
            className="text-[13px] leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {BOILERPLATE[active]}
          </div>
          <div
            className="text-[12px] mt-4"
            style={{ color: "var(--text-secondary)" }}
          >
            <strong>Media contact:</strong> press@ds-260usa.com
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section
      className="py-16 border-t"
      style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      data-testid="presskit-contact-section"
    >
      <div className="mx-auto max-w-4xl px-6 text-center">
        <div className="pill pill-ai mb-3 inline-flex">Say hello</div>
        <h2
          className="font-display text-2xl sm:text-3xl font-extrabold mb-2"
          style={{ color: "var(--text-primary)" }}
        >
          {UI_LABELS.contactTitle}
        </h2>
        <p
          className="text-sm max-w-lg mx-auto mb-6"
          style={{ color: "var(--text-secondary)" }}
        >
          {UI_LABELS.contactSub}
        </p>
        <a
          href="mailto:press@ds-260usa.com"
          className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3"
          data-testid="presskit-media-email"
        >
          <Mail className="w-4 h-4" /> press@ds-260usa.com
        </a>
        <div
          className="text-xs mt-6"
          style={{ color: "var(--text-secondary)" }}
        >
          We typically reply within one business day.
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      className="border-t py-8"
      style={{ borderColor: "var(--border-stone)", background: "#FFFFFF" }}
    >
      <div
        className="mx-auto max-w-6xl px-6 flex flex-wrap gap-4 justify-between items-center text-xs"
        style={{ color: "var(--text-secondary)" }}
      >
        <div>© {new Date().getFullYear()} DS-260USA.com — Press Kit</div>
        <div className="flex gap-4 flex-wrap">
          <Link to="/" className="hover:opacity-80">Home</Link>
          <Link to="/pricing" className="hover:opacity-80">Pricing</Link>
          <Link to="/legal/terms" className="hover:opacity-80">Terms</Link>
          <Link to="/legal/privacy" className="hover:opacity-80">Privacy</Link>
        </div>
      </div>
    </footer>
  );
}

export default function PressKitPage() {
  // Use the current origin (works locally, on preview, and in production).
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://ds-260usa.com";

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }} data-testid="presskit-page">
      <TopBar />
      <PressKitHero />
      <ShareableUrlsSection origin={origin} />
      <LogosSection origin={origin} />
      <BoilerplateSection />
      <PressReleaseSection />
      <ContactSection />
      <Footer />
    </div>
  );
}
